import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/primitives";
import { Cta } from "@/components/Cta";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { CHALLENGE_OPTIONS, ASSESSMENT_CHECKLIST } from "@/data/content";
import { submitContactForm } from "@/lib/contactApi";

const empty = {
    name: "",
    company: "",
    email: "",
    phone: "",
    challenge: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(empty);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const validate = () => {
        const err = {};
        if (!form.name.trim()) err.name = "Please enter your name";
        if (!form.company.trim()) err.company = "Please enter your company";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            err.email = "Please enter a valid email";
        if (!form.challenge) err.challenge = "Please select a challenge";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Please complete the required fields.");
            return;
        }

        setSubmitting(true);
        try {
            await submitContactForm(form);
            setSubmitted(true);
            toast.success("Assessment request received. We'll be in touch shortly.");
        } catch (error) {
            if (error.errors) setErrors(error.errors);
            toast.error(error.message || "Unable to send your request.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Contact"
                titleLines={["Book your free", "automation assessment"]}
                description="Tell us about your operation. We'll identify repetitive workflows and highlight where AI can create measurable value, with no obligation."
            />

            <section className="py-20 md:py-28">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <div className="grid gap-14 lg:grid-cols-12">
                        {/* Left info */}
                        <div className="lg:col-span-4">
                            <Reveal>
                                <h2 className="text-2xl font-semibold tracking-tight">
                                    What happens next
                                </h2>
                                <ul className="mt-6 space-y-4">
                                    {ASSESSMENT_CHECKLIST.map((item) => (
                                        <li
                                            key={item}
                                            className="flex items-start gap-3"
                                        >
                                            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-navy/10 text-navy">
                                                <Check
                                                    className="h-3 w-3"
                                                    strokeWidth={2.5}
                                                />
                                            </span>
                                            <span className="text-[15px] text-foreground">
                                                {item}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-10 space-y-4 border-t border-border pt-8 text-sm">
                                    <a
                                        href="mailto:support@harbrflow.com"
                                        data-testid="contact-email-link"
                                        className="flex items-center gap-3 text-muted-foreground transition-colors hover:text-foreground"
                                    >
                                        <Mail className="h-4 w-4 text-navy" strokeWidth={1.5} />
                                        support@harbrflow.com
                                    </a>
                                    <span className="flex items-center gap-3 text-muted-foreground">
                                        <Clock className="h-4 w-4 text-navy" strokeWidth={1.5} />
                                        Response within one business day
                                    </span>
                                    <span className="flex items-center gap-3 text-muted-foreground">
                                        <MapPin className="h-4 w-4 text-navy" strokeWidth={1.5} />
                                        Working with teams worldwide
                                    </span>
                                </div>
                            </Reveal>
                        </div>

                        {/* Form */}
                        <div className="lg:col-span-7 lg:col-start-6">
                            <Reveal delay={0.1}>
                                <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
                                    <AnimatePresence mode="wait">
                                        {submitted ? (
                                            <motion.div
                                                key="success"
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{
                                                    duration: 0.6,
                                                    ease: [0.16, 1, 0.3, 1],
                                                }}
                                                data-testid="contact-success"
                                                className="flex min-h-[420px] flex-col items-start justify-center"
                                            >
                                                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-navy/10 text-navy">
                                                    <Check
                                                        className="h-7 w-7"
                                                        strokeWidth={2}
                                                    />
                                                </span>
                                                <h3 className="mt-6 text-3xl font-bold tracking-tight">
                                                    Thank you, {form.name.split(" ")[0]}.
                                                </h3>
                                                <p className="mt-4 max-w-md text-lg leading-relaxed text-muted-foreground">
                                                    Your assessment request has
                                                    been received. A specialist
                                                    will reach out within one
                                                    business day to schedule your
                                                    session.
                                                </p>
                                                <Cta
                                                    onClick={() => {
                                                        setForm(empty);
                                                        setSubmitted(false);
                                                    }}
                                                    variant="secondary"
                                                    className="mt-8"
                                                    testId="contact-reset"
                                                    withArrow={false}
                                                >
                                                    Submit another request
                                                </Cta>
                                            </motion.div>
                                        ) : (
                                            <motion.form
                                                key="form"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                onSubmit={onSubmit}
                                                data-testid="contact-form"
                                                noValidate
                                                className="space-y-6"
                                            >
                                                <div className="grid gap-6 sm:grid-cols-2">
                                                    <Field
                                                        id="name"
                                                        label="Name"
                                                        value={form.name}
                                                        onChange={set("name")}
                                                        error={errors.name}
                                                        placeholder="Jane Doe"
                                                        disabled={submitting}
                                                    />
                                                    <Field
                                                        id="company"
                                                        label="Company"
                                                        value={form.company}
                                                        onChange={set("company")}
                                                        error={errors.company}
                                                        placeholder="Acme Freight"
                                                        disabled={submitting}
                                                    />
                                                    <Field
                                                        id="email"
                                                        label="Email"
                                                        type="email"
                                                        value={form.email}
                                                        onChange={set("email")}
                                                        error={errors.email}
                                                        placeholder="jane@company.com"
                                                        disabled={submitting}
                                                    />
                                                    <Field
                                                        id="phone"
                                                        label="Phone"
                                                        optional
                                                        value={form.phone}
                                                        onChange={set("phone")}
                                                        placeholder="+1 555 000 0000"
                                                        disabled={submitting}
                                                    />
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium">
                                                        Biggest Operational
                                                        Challenge
                                                    </Label>
                                                    <Select
                                                        value={form.challenge}
                                                        onValueChange={set(
                                                            "challenge",
                                                        )}
                                                        disabled={submitting}
                                                    >
                                                        <SelectTrigger
                                                            data-testid="contact-challenge"
                                                            className="h-11 rounded-xl"
                                                        >
                                                            <SelectValue placeholder="Select the area that hurts most" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {CHALLENGE_OPTIONS.map(
                                                                (o) => (
                                                                    <SelectItem
                                                                        key={o}
                                                                        value={o}
                                                                    >
                                                                        {o}
                                                                    </SelectItem>
                                                                ),
                                                            )}
                                                        </SelectContent>
                                                    </Select>
                                                    {errors.challenge && (
                                                        <p className="text-sm text-destructive">
                                                            {errors.challenge}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="space-y-2">
                                                    <Label
                                                        htmlFor="message"
                                                        className="text-sm font-medium"
                                                    >
                                                        Tell us more
                                                        <span className="ml-1 text-muted-foreground">
                                                            (optional)
                                                        </span>
                                                    </Label>
                                                    <Textarea
                                                        id="message"
                                                        data-testid="contact-message"
                                                        value={form.message}
                                                        onChange={set("message")}
                                                        placeholder="Share a bit about your current workflow or systems."
                                                        className="min-h-[120px] rounded-xl"
                                                        disabled={submitting}
                                                    />
                                                </div>

                                                <Cta
                                                    type="submit"
                                                    size="lg"
                                                    className="w-full"
                                                    testId="contact-submit"
                                                    withArrow={false}
                                                    disabled={submitting}
                                                >
                                                    {submitting
                                                        ? "Sending…"
                                                        : "Book Assessment"}
                                                </Cta>
                                                <p className="text-center font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                                    No obligation · No sales
                                                    presentation
                                                </p>
                                            </motion.form>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

function Field({ id, label, optional, error, ...props }) {
    return (
        <div className="space-y-2">
            <Label htmlFor={id} className="text-sm font-medium">
                {label}
                {optional && (
                    <span className="ml-1 text-muted-foreground">(optional)</span>
                )}
            </Label>
            <Input
                id={id}
                data-testid={`contact-${id}`}
                className="h-11 rounded-xl"
                {...props}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
    );
}
